import React, { useCallback, useEffect, useState } from 'react';
import Menu from 'antd/es/menu';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { push } from 'connected-react-router';
import { Link, matchPath } from 'react-router-dom';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

import {
  TopMenu,
  MenuDivider,
  TopMenuItem,
  GolfCourseLogo,
  TopBarContainer,
  GolfCourseTitle,
  HorizontalTopBar,
  MobileMenuDrawer,
  GolfCourseContainer,
} from './elements';

import { Skeleton } from 'components/skeleton';
import { PhoneIcon } from 'components/icons/phone-icon';
import { getProfileName } from 'models/profile/selectors';
import { BackToIcon } from 'components/icons/back-to-icon';
import { PathCreator, RoutePaths } from 'routing/constants';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { getProfileMenu, ProfileMenu } from '../profile-menu';
import { courseWebsite } from 'modules/api-requests/course/constants';
import { MobileMenuIcon } from 'components/icons/menu/mobile-menu-icon';
import { getCourseSelector, isCourseLoadingSelector } from 'models/course/selectors';

const defaultCourseURL = '/images/default-course.png';

export const TopBar = () => {
  const dispatch = useAppDispatch();
  const profileName = useSelector(getProfileName);
  const course = useAppSelector(getCourseSelector);
  const isCourseLoading = useAppSelector(isCourseLoadingSelector);
  const breakpoints = useBreakpoint();
  const { location } = useHistory();
  const { pathname } = location;
  const mainPathMatch = matchPath(pathname, { path: PathCreator[RoutePaths.BOOKING_LIST].path });
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const onContactUsClick = useCallback(() => {
    setShowMobileMenu(false);
    dispatch(push(PathCreator[RoutePaths.CONTACT_US].getUrl()));
  }, [dispatch]);

  const useMobileVersion = !breakpoints.xl;
  const isAuthItemsHidden = !breakpoints.md && useMobileVersion;

  const triggerMobileMenu = () => {
    setShowMobileMenu((state) => !state);
  };

  const changeMenuOpened = (open: boolean) => setShowMobileMenu(open);
  const onMenuClose = () => setShowMobileMenu(false);

  const onProfileMenuClick = useCallback(
    ({ key }) => {
      setShowMobileMenu(false);
      dispatch(push(key));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!useMobileVersion) {
      setShowMobileMenu(false);
    }
  }, [useMobileVersion]);

  const isLoading = !course?.id || isCourseLoading;

  return (
    <TopBarContainer>
      <HorizontalTopBar>
        <GolfCourseContainer>
          {isLoading || !course?.id ? (
            <Skeleton $height={40} $width={40} />
          ) : (
            <Link to={PathCreator[RoutePaths.BOOKING_LIST].getUrl()}>
              <GolfCourseLogo src={course?.logoUrl || defaultCourseURL} alt="logo" />
            </Link>
          )}

          <GolfCourseTitle>
            {isLoading && <Skeleton $height={40} $width={200} />}
            {mainPathMatch?.isExact ? (
              course?.name
            ) : (
              <Link to={PathCreator[RoutePaths.BOOKING_LIST].getUrl()}>{course?.name}</Link>
            )}
          </GolfCourseTitle>
        </GolfCourseContainer>

        <TopMenu>
          {useMobileVersion ? (
            <MobileMenuIcon onClick={triggerMobileMenu} />
          ) : (
            <>
              <TopMenuItem onClick={onContactUsClick}>
                <PhoneIcon /> Contact Us
              </TopMenuItem>
              <TopMenuItem>
                <a href={courseWebsite}>
                  <BackToIcon /> Back to the course website
                </a>
              </TopMenuItem>
              <MenuDivider type="vertical" />
            </>
          )}

          {!isAuthItemsHidden ? <ProfileMenu onClickCallback={onMenuClose} /> : null}
        </TopMenu>
      </HorizontalTopBar>

      <MobileMenuDrawer
        width={322}
        placement="right"
        closable={true}
        maskClosable={true}
        onClose={onMenuClose}
        open={showMobileMenu}
        title={profileName ?? 'Guest'}
        afterOpenChange={changeMenuOpened}
      >
        {profileName ? getProfileMenu(onProfileMenuClick, !!course?.waitlistActive) : null}

        <Menu onClick={onMenuClose}>
          {isAuthItemsHidden && profileName ? <Menu.Divider /> : null}
          <Menu.Item key="contactus" onClick={onContactUsClick}>
            <PhoneIcon /> Contact Us
          </Menu.Item>
          <Menu.Item key="to-course">
            <a href={courseWebsite}>
              <BackToIcon /> Back to the course website
            </a>
          </Menu.Item>

          {isAuthItemsHidden && !profileName ? (
            <>
              <Menu.Divider />
              <Menu.Item key="login">
                <Link to={PathCreator[RoutePaths.AUTH_LOGIN].getUrl()}>Log In</Link>
              </Menu.Item>
              <Menu.Item key="signup">
                <Link to={PathCreator[RoutePaths.AUTH_SIGNUP].getUrl()}>Sign Up</Link>
              </Menu.Item>
            </>
          ) : null}
        </Menu>
      </MobileMenuDrawer>
    </TopBarContainer>
  );
};

